/**
 * ProposalStateScreen - Telas de estado da landing pública de propostas
 *
 * Cobre carregamento (skeleton no formato do layout premium) e indisponibilidade
 * do link (token inválido/expirado), mantendo a mesma linguagem visual.
 *
 * @version 1.1.0
 * RELEASE: 2026-08-25 v1.1.0 - Nova tela ProposalRevokedScreen (proposta substituída /
 *          cancelada / link desativado) com atalho para a versão mais recente
 * RELEASE: 2026-08-11 v1.0.0 - Criação inicial (redesign premium da landing /p/:token)
 */

import React from 'react';
import { AlertTriangle, ArrowRight, Ban } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';

/** Skeleton exibido enquanto a Edge Function resolve o token */
export function ProposalLoadingScreen() {
  return (
    <div className="proposal-page min-h-screen px-4 py-8 md:py-14">
      <div className="mx-auto max-w-4xl space-y-5">
        <Skeleton className="h-[22rem] w-full rounded-3xl" />
        <Skeleton className="h-56 w-full rounded-2xl" />
        <Skeleton className="h-32 w-full rounded-2xl" />
      </div>
    </div>
  );
}

/** Tela de link inválido/indisponível */
export function ProposalUnavailableScreen({ message }: { message?: string | null }) {
  return (
    <div className="proposal-page flex min-h-screen items-center justify-center px-4">
      <div className="proposal-surface w-full max-w-md space-y-3 p-8 text-center">
        <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-secondary">
          <AlertTriangle className="h-5 w-5 text-amber-500" />
        </div>
        <h1 className="text-lg font-semibold">Proposta indisponível</h1>
        <p className="text-sm text-muted-foreground">
          {message ||
            'Este link não é mais válido. Entre em contato com o responsável comercial para receber uma nova versão.'}
        </p>
      </div>
    </div>
  );
}

/**
 * Tela de LINK REVOGADO (v1.1.0)
 *
 * Exibida quando a proposta foi substituída por uma nova versão ou cancelada.
 * Não mostra itens nem valores — apenas orienta o cliente e, no caso de
 * substituição, oferece o link da versão mais recente (quando ativa).
 */
export function ProposalRevokedScreen({
  reason,
  proposalNumber,
  tenantName,
  successor,
}: {
  reason: 'superseded' | 'cancelled' | 'disabled';
  proposalNumber?: string | null;
  tenantName?: string | null;
  successor?: { proposal_number: string; token: string | null } | null;
}) {
  // Texto por motivo da revogação
  const copy = {
    superseded: {
      title: 'Proposta substituída',
      description:
        'Esta proposta foi substituída por uma versão mais recente. O link antigo não é mais válido.',
    },
    cancelled: {
      title: 'Proposta cancelada',
      description:
        'Esta proposta foi cancelada e o link de acesso foi desativado. Entre em contato com o responsável comercial.',
    },
    disabled: {
      title: 'Link desativado',
      description:
        'Este link não é mais válido. Entre em contato com o responsável comercial para receber uma nova versão.',
    },
  }[reason];

  const successorUrl = successor?.token ? `${window.location.origin}/p/${successor.token}` : null;

  return (
    <div className="proposal-page flex min-h-screen items-center justify-center px-4">
      <div className="proposal-surface w-full max-w-md space-y-4 p-8 text-center">
        <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-secondary">
          <Ban className="h-5 w-5 text-muted-foreground" />
        </div>

        <div className="space-y-1">
          <h1 className="text-lg font-semibold">{copy.title}</h1>
          {proposalNumber && (
            <p className="font-mono text-xs text-muted-foreground">{proposalNumber}</p>
          )}
        </div>

        <p className="text-sm text-muted-foreground">{copy.description}</p>

        {/* Substituída: aponta a versão mais recente */}
        {reason === 'superseded' && successor && (
          <div className="space-y-3 rounded-xl border border-border/60 p-4">
            <p className="text-sm">
              Nova proposta:{' '}
              <span className="font-mono font-medium">{successor.proposal_number}</span>
            </p>
            {successorUrl ? (
              <Button asChild size="sm" className="w-full">
                <a href={successorUrl}>
                  Ver proposta atual
                  <ArrowRight className="ml-1.5 h-4 w-4" />
                </a>
              </Button>
            ) : (
              <p className="text-xs text-muted-foreground">
                Solicite o link da nova proposta ao responsável comercial.
              </p>
            )}
          </div>
        )}

        {tenantName && <p className="text-xs text-muted-foreground">{tenantName}</p>}
      </div>
    </div>
  );
}
